export type BoardMeta = {
  id: string;
  title: string;
  projectId?: string | null;
  createdAt: number;
  updatedAt: number;
};

/** Remote file pointer (no dataURL). */
export type RemoteFileRef = {
  id: string;
  mimeType: string;
  created: number;
  version?: number;
  key: string;
};

export type BoardScenePayload = {
  elements: unknown[];
  appState: Record<string, unknown> | null;
  files?: Record<string, RemoteFileRef | Record<string, unknown>>;
};

type BoardsRuntimeConfig = {
  apiUrl?: string;
  apiToken?: string;
};

declare global {
  interface Window {
    __BOARDS_CONFIG__?: BoardsRuntimeConfig;
  }
}

const getConfig = () => {
  const runtime = window.__BOARDS_CONFIG__ || {};
  const baseUrl = (
    runtime.apiUrl ||
    import.meta.env.VITE_APP_BOARDS_API_URL ||
    ""
  ).replace(/\/+$/, "");
  const token =
    runtime.apiToken || import.meta.env.VITE_APP_BOARDS_API_TOKEN || "";
  return { baseUrl, token, enabled: Boolean(baseUrl && token) };
};

export const isBoardsApiEnabled = () => getConfig().enabled;

export const boardFileObjectKey = (boardId: string, fileId: string) =>
  `boards/${boardId}/files/${fileId}`;

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const { baseUrl, token, enabled } = getConfig();
  if (!enabled) {
    throw new Error("Boards API is not configured");
  }

  const headers = new Headers(init.headers || {});
  if (!headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  // Only default JSON content-type when sending a body that isn't FormData/Blob
  if (
    init.body &&
    !(init.body instanceof Blob) &&
    !(init.body instanceof ArrayBuffer) &&
    !(init.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Drawings API ${response.status}: ${body || response.statusText}`,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  return undefined as T;
};

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });

const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
  const response = await fetch(dataURL);
  return response.blob();
};

export const boardsApi = {
  getSettings: async () => {
    const data = await request<{ activeBoardId: string | null }>(
      "/api/settings",
    );
    return { activeBoardId: data.activeBoardId };
  },

  setActiveBoard: async (activeBoardId: string) => {
    const data = await request<{ activeBoardId: string | null }>(
      "/api/settings",
      {
        method: "PUT",
        body: JSON.stringify({ activeBoardId }),
      },
    );
    return { activeBoardId: data.activeBoardId };
  },

  list: async () => {
    const data = await request<{ drawings: BoardMeta[] }>("/api/drawings");
    return { boards: data.drawings };
  },

  create: async (
    payload: {
      title?: string;
      id?: string;
      content?: BoardScenePayload;
      projectId?: string | null;
    } = {},
  ) => {
    const data = await request<{ drawing: BoardMeta }>("/api/drawings", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return { board: data.drawing };
  },

  update: async (
    id: string,
    payload: {
      title?: string;
      content?: BoardScenePayload;
      projectId?: string | null;
    },
  ) => {
    const data = await request<{
      drawing: BoardMeta & { content?: BoardScenePayload };
    }>(`/api/drawings/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return { board: data.drawing };
  },

  duplicate: async (
    id: string,
    opts?: { title?: string; projectId?: string | null },
  ) => {
    const scene = await boardsApi.getScene(id);
    return boardsApi.create({
      title: opts?.title?.trim() || "未命名画板 副本",
      projectId: opts?.projectId ?? null,
      content: scene,
    });
  },

  remove: (id: string) =>
    request<{ ok: true }>(`/api/drawings/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  getScene: async (id: string): Promise<BoardScenePayload> => {
    const data = await request<{
      drawing: BoardMeta & { content: BoardScenePayload };
    }>(`/api/drawings/${encodeURIComponent(id)}`);
    return (
      data.drawing.content || {
        elements: [],
        appState: {},
        files: {},
      }
    );
  },

  putScene: async (
    id: string,
    scene: BoardScenePayload,
    opts?: { keepalive?: boolean },
  ) => {
    if (opts?.keepalive) {
      const { baseUrl, token, enabled } = getConfig();
      if (!enabled) {
        throw new Error("Boards API is not configured");
      }
      const response = await fetch(
        `${baseUrl}/api/drawings/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          keepalive: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content: scene }),
        },
      );
      if (!response.ok) {
        const body = await response.text();
        throw new Error(
          `Drawings API ${response.status}: ${body || response.statusText}`,
        );
      }
      const data = (await response.json()) as {
        drawing: BoardMeta;
      };
      return {
        ok: true as const,
        updatedAt: data.drawing.updatedAt,
      };
    }

    const result = await boardsApi.update(id, { content: scene });
    return {
      ok: true as const,
      updatedAt: result.board.updatedAt,
    };
  },

  putFile: async (
    boardId: string,
    file: {
      id: string;
      mimeType: string;
      dataURL: string;
      created: number;
      version?: number;
    },
  ): Promise<RemoteFileRef> => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Boards API is not configured");
    }
    const blob = await dataURLToBlob(file.dataURL);
    const response = await fetch(
      `${baseUrl}/api/drawings/${encodeURIComponent(
        boardId,
      )}/files/${encodeURIComponent(file.id)}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": file.mimeType || blob.type || "application/octet-stream",
          "X-File-Created": String(file.created || Date.now()),
          "X-File-Version": String(file.version || 1),
        },
        body: blob,
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Drawings API ${response.status}: ${body || response.statusText}`,
      );
    }
    const data = (await response.json()) as { file: RemoteFileRef };
    return data.file;
  },

  getFile: async (
    boardId: string,
    fileId: string,
  ): Promise<{
    dataURL: string;
    mimeType: string;
    created: number;
    version: number;
  }> => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Boards API is not configured");
    }
    const response = await fetch(
      `${baseUrl}/api/drawings/${encodeURIComponent(
        boardId,
      )}/files/${encodeURIComponent(fileId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Drawings API ${response.status}: ${body || response.statusText}`,
      );
    }
    const blob = await response.blob();
    const mimeType =
      response.headers.get("content-type") ||
      blob.type ||
      "application/octet-stream";
    const created = Number(response.headers.get("X-File-Created")) || Date.now();
    const version = Number(response.headers.get("X-File-Version")) || 1;
    const dataURL = await blobToDataURL(blob);
    return { dataURL, mimeType, created, version };
  },

  fetchPreviewBlob: async (boardId: string) => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Boards API is not configured");
    }
    const response = await fetch(
      `${baseUrl}/api/drawings/${encodeURIComponent(boardId)}/preview`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Preview fetch failed: ${response.status}`);
    }
    return response.blob();
  },

  putPreview: async (boardId: string, blob: Blob) => {
    const { baseUrl, token, enabled } = getConfig();
    if (!enabled) {
      throw new Error("Boards API is not configured");
    }
    const response = await fetch(
      `${baseUrl}/api/drawings/${encodeURIComponent(boardId)}/preview`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "image/png",
        },
        body: blob,
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Preview upload ${response.status}: ${body || response.statusText}`,
      );
    }
    return { ok: true as const };
  },
};
